import React, { useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import PermissionButton from '../components/PermissionButton';
import { 
  useGetConsultantsQuery, 
  useGetConsultantQuery, 
  useAddConsultantMutation, 
  useUpdateConsultantMutation, 
  useDeleteConsultantMutation,
  useGetConsultantAdminsQuery
} from '../store/api';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import { usePermission } from '../hooks/useAuth';

export default function Consultants() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [viewingConsultant, setViewingConsultant] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  
  // Permission hooks - strictly permission-based
  const canAddConsultant = usePermission('consultant.add');
  const canEditConsultant = usePermission('consultant.edit');
  const canDeleteConsultant = usePermission('consultant.delete');
  const canViewConsultant = usePermission('consultant.view'); // Already being used in route protection
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    admin_id: '',
    position: '',
    department: '',
    salary: '',
    address: '',
    education: '',
    experience: '',
    languages: '',
    specialization: '',
    notes: '',
    emergency_contact: '',
    emergency_phone: '',
    joining_date: '',
    is_active: true
  });

  // API hooks
  const { data: consultantsData, isLoading: consultantsLoading, error: consultantsError } = useGetConsultantsQuery();
  const { data: consultantAdminsData, isLoading: adminsLoading } = useGetConsultantAdminsQuery();
  const [addConsultant, { isLoading: addLoading }] = useAddConsultantMutation();
  const [updateConsultant, { isLoading: updateLoading }] = useUpdateConsultantMutation();
  const [deleteConsultant, { isLoading: deleteLoading }] = useDeleteConsultantMutation();

  // Main loading state for the component
  const isLoading = consultantsLoading || adminsLoading;

  const consultants = consultantsData?.data || [];
  const consultantAdmins = Array.isArray(consultantAdminsData?.data) 
    ? consultantAdminsData.data 
    : Array.isArray(consultantAdminsData) 
    ? consultantAdminsData 
    : [];

  // Get admin IDs that are already assigned as consultants
  const getAssignedAdminIds = () => {
    return consultants
      .filter(consultant => {
        // When editing, exclude the current consultant from the check
        if (editingConsultant && consultant.id === editingConsultant.id) {
          return false;
        }
        return consultant.admin_id;
      })
      .map(consultant => parseInt(consultant.admin_id));
  };

  // Check if admin is already assigned as consultant
  const isAdminAlreadyConsultant = (adminId) => {
    const assignedIds = getAssignedAdminIds();
    return assignedIds.includes(parseInt(adminId));
  };

  // Debug API response
  React.useEffect(() => {
    if (consultantAdminsData) {
      console.log('Consultant Admins Data:', consultantAdminsData);
      console.log('Type:', typeof consultantAdminsData);
      console.log('Is Array:', Array.isArray(consultantAdminsData));
    }
  }, [consultantAdminsData]);

  const filteredConsultants = consultants.filter(consultant => {
    const adminName = consultant.admin ? `${consultant.admin.first_name || ''} ${consultant.admin.last_name || ''}`.trim() : '';
    const adminEmail = consultant.admin?.email || '';
    const matchesSearch = adminName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         adminEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || 
                         (statusFilter === 'active' && consultant.is_active) ||
                         (statusFilter === 'inactive' && !consultant.is_active);
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalItems = filteredConsultants.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedConsultants = filteredConsultants.slice(startIndex, endIndex);

  // Get consultant statistics
  const totalConsultants = consultants.length;
  const activeConsultants = consultants.filter(c => c.is_active).length;
  const inactiveConsultants = consultants.filter(c => !c.is_active).length;
  const newConsultants = consultants.filter(c => {
    const joiningDate = new Date(c.joining_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return joiningDate >= thirtyDaysAgo;
  }).length;

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setEditingConsultant(null);
    setFormData({
      admin_id: '',
      position: '',
      department: '',
      salary: '',
      address: '',
      education: '',
      experience: '',
      languages: '',
      specialization: '',
      notes: '',
      emergency_contact: '',
      emergency_phone: '',
      joining_date: '',
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleEdit = (consultant) => {
    setEditingConsultant(consultant);
    setFormData({
      admin_id: consultant.admin_id || '',
      position: consultant.position || '',
      department: consultant.department || '',
      salary: consultant.salary || '',
      address: consultant.address || '',
      education: consultant.education || '',
      experience: consultant.experience || '',
      languages: consultant.languages || '',
      specialization: consultant.specialization || '',
      notes: consultant.notes || '',
      emergency_contact: consultant.emergency_contact || '',
      emergency_phone: consultant.emergency_phone || '',
      joining_date: consultant.joining_date ? 
        (consultant.joining_date.includes('T') 
          ? consultant.joining_date.split('T')[0] 
          : consultant.joining_date.match(/^\d{4}-\d{2}-\d{2}$/) 
            ? consultant.joining_date 
            : '') 
        : '',
      is_active: consultant.is_active !== undefined ? consultant.is_active : true
    });
    setIsModalOpen(true);
  };

  const handleView = (consultant) => {
    setViewingConsultant(consultant);
    setActiveTab('details');
    setIsViewModalOpen(true);
  };

  const handleDelete = async (consultantId, consultantName) => {
    if (window.confirm(`"${consultantName}" konsultantını silmək istədiyinizə əminsiniz?`)) {
      try {
        await deleteConsultant(consultantId).unwrap();
        toast.success('Konsultant uğurla silindi');
      } catch (error) {
        toast.error('Silmə zamanı xəta baş verdi');
        console.error('Delete error:', error);
      }
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingConsultant) {
        await updateConsultant({ id: editingConsultant.id, obj: formData }).unwrap();
        toast.success('Konsultant uğurla yeniləndi');
      } else {
        await addConsultant(formData).unwrap();
        toast.success('Konsultant uğurla əlavə olundu');
      }
      setIsModalOpen(false);
      setEditingConsultant(null);
    } catch (error) {
      toast.error('Əməliyyat zamanı xəta baş verdi');
      console.error('Submit error:', error);
    }
  };

  // Get students for a specific consultant
  const getConsultantStudents = (consultantId) => {
    const consultant = consultants.find(c => c.id === consultantId);
    return consultant?.students || [];
  };

  if (consultantsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Konsultantlar yüklənir...</div>
      </div>
    );
  }

  if (consultantsError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Konsultantlar yüklənərkən xəta baş verdi</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Konsultantlar</h1>
          <p className="mt-1 text-sm text-gray-600">
            Şirkətdə işləyən konsultantları idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {consultants.length}
            </span>
            {(searchTerm || statusFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {filteredConsultants.length}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0">
          {canAddConsultant && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Konsultant
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Ümumi Konsultantlar</p>
              <p className="text-3xl font-bold">{totalConsultants}</p>
            </div>
            <div className="bg-blue-400 bg-opacity-30 rounded-lg p-3">
              <UsersIcon className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Aktiv Konsultantlar</p>
              <p className="text-3xl font-bold">{activeConsultants}</p>
            </div>
            <div className="bg-green-400 bg-opacity-30 rounded-lg p-3">
              <CheckCircleIcon className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Qeyri-aktiv</p>
              <p className="text-3xl font-bold">{inactiveConsultants}</p>
            </div>
            <div className="bg-red-400 bg-opacity-30 rounded-lg p-3">
              <XCircleIcon className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Yeni (30 gün)</p>
              <p className="text-3xl font-bold">{newConsultants}</p>
            </div>
            <div className="bg-orange-400 bg-opacity-30 rounded-lg p-3">
              <ClockIcon className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Ad, soyad və ya email ilə axtarış..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            <option value="active">Yalnız aktiv</option>
            <option value="inactive">Yalnız qeyri-aktiv</option>
          </select>
        </div>
        <div>
          <button
            onClick={handleClearFilters}
            disabled={!searchTerm && !statusFilter}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Filtri təmizlə
          </button>
        </div>
      </div>

      {/* Consultants table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          {/* Table controls */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Səhifədə göstər:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="text-sm text-gray-700">
              {totalItems > 0 ? (
                <>
                  {startIndex + 1}-{Math.min(endIndex, totalItems)} / {totalItems} nəticə
                </>
              ) : (
                'Nəticə tapılmadı'
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Konsultant</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əlaqə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    İşə başlama
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Tələbələr
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {!isLoading && paginatedConsultants.length > 0 && paginatedConsultants.map((consultant) => (
                  <tr key={consultant.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                                              <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-4">
                            <span className="text-sm font-bold text-white">
                              {consultant.admin?.first_name && consultant.admin?.last_name 
                                ? `${consultant.admin.first_name[0]}${consultant.admin.last_name[0]}` 
                                : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                              {consultant.admin?.first_name && consultant.admin?.last_name 
                                ? `${consultant.admin.first_name} ${consultant.admin.last_name}` 
                                : 'Admin tapılmadı'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {consultant.id}
                            </div>
                          </div>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center space-x-1 mb-1">
                          <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                          <span>{consultant.admin?.email || 'Məlumat yoxdur'}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <PhoneIcon className="h-3 w-3 text-gray-400" />
                          <span>{consultant.admin?.phone || 'Məlumat yoxdur'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{consultant.joining_date ? new Date(consultant.joining_date).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {getConsultantStudents(consultant.id).length} tələbə
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        consultant.is_active 
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {consultant.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {canViewConsultant && (
                          <button
                            onClick={() => handleView(consultant)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEditConsultant && (
                          <button
                            onClick={() => handleEdit(consultant)}
                            className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Redaktə et"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteConsultant && (
                          <button
                            onClick={() => handleDelete(consultant.id, 
                              consultant.admin?.first_name && consultant.admin?.last_name 
                                ? `${consultant.admin.first_name} ${consultant.admin.last_name}` 
                                : 'Konsultant'
                            )}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Əvvəlki
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 3 ||
                      page === currentPage + 3
                    ) {
                      return (
                        <span key={page} className="px-2 py-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Növbəti
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
              
              <div className="text-sm text-gray-700">
                Səhifə {currentPage} / {totalPages}
              </div>
            </div>
          )}

          {!isLoading && filteredConsultants.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <UserIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Konsultant tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni konsultant əlavə edin.
              </p>
              {canAddConsultant && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Konsultantı Əlavə Et
                </button>
              )}
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-16">
              <Audio
                height="80"
                width="80"
                radius="9"
                color="blue"
                ariaLabel="loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </div>
          )}
        </div>
      </div>

      {/* View Modal - Will be added in the next part */}
      {isViewModalOpen && viewingConsultant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <span className="text-sm font-bold text-white">
                      {viewingConsultant.admin?.first_name && viewingConsultant.admin?.last_name 
                        ? `${viewingConsultant.admin.first_name[0]}${viewingConsultant.admin.last_name[0]}` 
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {viewingConsultant.admin?.first_name && viewingConsultant.admin?.last_name 
                        ? `${viewingConsultant.admin.first_name} ${viewingConsultant.admin.last_name}` 
                        : 'Admin tapılmadı'} - Ətraflı Məlumat
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      viewingConsultant.is_active 
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {viewingConsultant.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Konsultant Təfərrüatları
                  </button>
                  <button
                    onClick={() => setActiveTab('students')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'students'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Müraciətlər ({getConsultantStudents(viewingConsultant.id).length})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-64">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-white" />
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">Şəxsi Məlumatlar</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Admin Ad Soyad</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {viewingConsultant.admin?.first_name && viewingConsultant.admin?.last_name 
                              ? `${viewingConsultant.admin.first_name} ${viewingConsultant.admin.last_name}` 
                              : 'Məlumat yoxdur'}
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Admin Email</div>
                          <div className="text-sm text-blue-600">
                            {viewingConsultant.admin?.email ? (
                              <a href={`mailto:${viewingConsultant.admin.email}`} className="hover:underline">
                                {viewingConsultant.admin.email}
                              </a>
                            ) : (
                              'Məlumat yoxdur'
                            )}
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Admin Telefon</div>
                          <div className="text-sm text-blue-600">
                            {viewingConsultant.admin?.phone ? (
                              <a href={`tel:${viewingConsultant.admin.phone}`} className="hover:underline">
                                {viewingConsultant.admin.phone}
                              </a>
                            ) : (
                              'Məlumat yoxdur'
                            )}
                          </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Ünvan</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.address || 'Məlumat yoxdur'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 mt-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Status</div>
                          <div className="text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              viewingConsultant.is_active 
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                                : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                              {viewingConsultant.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Work Information */}
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-green-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-green-900">İş Məlumatları</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Vəzifə</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingConsultant.position || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Şöbə</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.department || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">İşə Başlama</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.joining_date ? new Date(viewingConsultant.joining_date).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Maaş</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingConsultant.salary || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Tələbə Sayı</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {getConsultantStudents(viewingConsultant.id).length} aktiv tələbə
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">İxtisaslaşma</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.specialization || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Dillər</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.languages || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Təcrübə</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.experience || 'Məlumat yoxdur'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Education & Emergency Contact */}
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-purple-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-purple-900">Təhsil və Fövqəladə Əlaqə</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Təhsil</div>
                          <div className="text-sm text-gray-900">{viewingConsultant.education || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Fövqəladə Əlaqə</div>
                          <div className="text-sm text-gray-900">
                            <div className="font-medium">{viewingConsultant.emergency_contact || 'Məlumat yoxdur'}</div>
                            {viewingConsultant.emergency_phone && (
                              <div className="text-purple-600 mt-1">
                                <a href={`tel:${viewingConsultant.emergency_phone}`} className="hover:underline">
                                  {viewingConsultant.emergency_phone}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {viewingConsultant.notes && (
                        <div className="mt-4">
                          <div className="bg-white p-4 rounded-lg border border-purple-100">
                            <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Qeydlər</div>
                            <div className="text-sm text-gray-900">{viewingConsultant.notes}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'students' && (
                  <div className="space-y-3">
                    {getConsultantStudents(viewingConsultant.id).length > 0 ? (
                      getConsultantStudents(viewingConsultant.id).map((student) => (
                        <div key={student.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">
                                  {student.firstName[0]}{student.lastName[0]}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div className="font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    student.statusId === 6 ? 'bg-green-100 text-green-800 border border-green-200' :
                                    student.statusId === 4 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    student.statusId === 3 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                    'bg-gray-100 text-gray-800 border border-gray-200'
                                  }`}>
                                    {student.status}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                  <span>📞 {student.phone}</span>
                                  <span>🎓 {student.specialization}</span>
                                  <span>📊 GPA: {student.gpa}</span>
                                  <span>🌍 {student.languageExam}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  <strong>Müraciət tarixi:</strong> {student.firstContactDate ? new Date(student.firstContactDate).toLocaleDateString('az-AZ') : 'Tarix yoxdur'} |
                                  <strong> Mənbə:</strong> {student.source} |
                                  <strong> Maraq dairəsi:</strong> {student.interestedCountries}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">
                                Maliyyə proqnozu
                              </div>
                              <div className="text-sm font-medium text-gray-900">
                                {student.financialForecast}
                              </div>
                              <div className="text-xs text-purple-600 mt-1">
                                {student.tuitionFeeRange}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="mx-auto h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                          <UserIcon className="h-6 w-6 text-purple-400" />
                        </div>
                        <h5 className="text-lg font-medium text-gray-900 mb-2">Müraciət tapılmadı</h5>
                        <p className="text-gray-500">Bu konsultanta hələ heç bir tələbə təyin edilməyib</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {canEditConsultant && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleEdit(viewingConsultant);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                    Redaktə Et
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal - Will continue in next part */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingConsultant ? 'Konsultantı Redaktə Et' : 'Yeni Konsultant Əlavə Et'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {editingConsultant ? 'Konsultant məlumatlarını yeniləyin' : 'Yeni konsultant məlumatlarını daxil edin'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Əsas Məlumatlar</h4>
                                    <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Admin Seçimi *
                      </label>
                      <select
                        required
                        value={formData.admin_id}
                        onChange={(e) => setFormData({ ...formData, admin_id: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Admin seçin...</option>
                        {consultantAdmins && consultantAdmins.length > 0 ? (
                          consultantAdmins.map(admin => {
                            const isAlreadyConsultant = isAdminAlreadyConsultant(admin.id);
                            return (
                              <option 
                                key={admin.id} 
                                value={admin.id}
                                disabled={isAlreadyConsultant}
                                style={isAlreadyConsultant ? { 
                                  color: '#9CA3AF', 
                                  backgroundColor: '#F3F4F6',
                                  fontStyle: 'italic'
                                } : {}}
                              >
                                {admin.first_name} {admin.last_name}
                                {isAlreadyConsultant ? ' (Artıq konsultantdır)' : ''}
                              </option>
                            );
                          })
                        ) : (
                          <option disabled>Consultant admin tapılmadı</option>
                        )}
                      </select>
                      {adminsLoading && (
                        <p className="text-sm text-gray-500 mt-1">Adminlər yüklənir...</p>
                      )}
                      {!adminsLoading && consultantAdmins && consultantAdmins.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-gray-600">
                            📝 Əlçatan adminlər: {consultantAdmins.filter(admin => !isAdminAlreadyConsultant(admin.id)).length}
                          </p>
                          <p className="text-xs text-gray-500">
                            ⚠️ Artıq konsultant olanlar seçilə bilməz
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ünvan
                      </label>
                      <textarea
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Məsələn: Bakı şəhəri, Nəsimi rayonu, Azadlıq prospekti 123"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Work Information Section */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <h4 className="text-lg font-semibold text-green-900 mb-4">İş Məlumatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vəzifə
                      </label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: Baş Konsultant"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Şöbə
                      </label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: Beynəlxalq Təhsil"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İşə Başlama Tarixi *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.joining_date}
                        onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maaş
                      </label>
                      <input
                        type="text"
                        value={formData.salary}
                        onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: 2000 AZN"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İxtisaslaşma
                      </label>
                      <input
                        type="text"
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: Almaniya, İngiltərə universitetləri"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dil Biliyi
                      </label>
                      <input
                        type="text"
                        value={formData.languages}
                        onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: Azərbaycan, İngilis, Rus"
                      />
                    </div>
                  </div>
                </div>

                {/* Education & Experience Section */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-4">Təhsil və Təcrübə</h4>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təhsil
                      </label>
                      <textarea
                        rows={2}
                        value={formData.education}
                        onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 resize-none"
                        placeholder="Məsələn: Bakı Dövlət Universiteti, Beynəlxalq Münasibətlər fakültəsi"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İş Təcrübəsi
                      </label>
                      <textarea
                        rows={2}
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 resize-none"
                        placeholder="Məsələn: 5 il beynəlxalq təhsil sahəsində təcrübə"
                      />
                    </div>
                  </div>
                </div>

                {/* Emergency Contact Section */}
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
                  <h4 className="text-lg font-semibold text-orange-900 mb-4">Fövqəladə Əlaqə</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Əlaqə Şəxsi
                      </label>
                      <input
                        type="text"
                        value={formData.emergency_contact}
                        onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Məsələn: Fatimə Məmmədova (Ana)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fövqəladə Telefon
                      </label>
                      <input
                        type="tel"
                        value={formData.emergency_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Məsələn: +994552345679"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Əlavə Qeydlər
                      </label>
                      <textarea
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 resize-none"
                        placeholder="Konsultant haqqında əlavə məlumatlar..."
                      />
                    </div>

                    <div className="md:col-span-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          Konsultant aktiv statusdadır
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingConsultant ? 'Yenilə' : 'Yadda saxla'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 